<script lang="ts">
	import { analytics } from '$lib/analytics';
	import PinControls from './PinControls.svelte';
	import type { Board, GeneratedImage } from '$lib/diffusionApi';
	import { onMount } from 'svelte';

	export let images: GeneratedImage[];
	export let htmlImages: HTMLImageElement[];

	export let displayImageIndex: number | undefined = undefined;
	export let displayImage: GeneratedImage | undefined = undefined;
	export let isLargeDisplayImageLoaded: boolean;

	export let index: number;
	export let image: GeneratedImage;
	export let assetSelectionIds: string[];
	export let clickAssetAction: 'view' | 'select';

	export let showPinControls: boolean;
	export let selectedBoard: Board | undefined = undefined;
	export let boardIdToBoardImagePinIdsMap: Map<string, string[]>;

	export let showSelectionBoxes: boolean;
	export let useMasonry: boolean;

	export let createPin: (image: GeneratedImage) => Promise<void>;
	export let deletePin: (image: GeneratedImage) => Promise<void>;
	export let clickBoardSelectorButton: (e: MouseEvent) => void;

	export let openImageModal: () => void;

	let imageWidth = 'w-[11rem]';

	const handleAction = () => {
		if (clickAssetAction === 'view') {
			analytics.capture('view_generated_image', {
				generation_id: image.generation_id
			});
			displayImageIndex = index;
			displayImage = image;
			isLargeDisplayImageLoaded = false;
			openImageModal();
		} else if (clickAssetAction === 'select') {
			if (assetSelectionIds.includes(image.generation_id)) {
				assetSelectionIds = assetSelectionIds.filter((_) => _ !== image.generation_id);
			} else {
				assetSelectionIds = [...assetSelectionIds, image.generation_id];
			}
		}
	};

	onMount(() => {
		const isAboveSmall = window.matchMedia('(min-width: 640px)').matches;
		if (images.length === 1) {
			if (isAboveSmall) {
				imageWidth = 'w-[33rem]';
			} else {
				imageWidth = 'w-[22rem]';
			}
		} else if (images.length === 2) {
			if (isAboveSmall) {
				imageWidth = 'w-[16.5rem]';
			} else {
				imageWidth = 'w-[11rem]';
			}
		}
	});
</script>

<div
	class={`flex flex-col justify-center items-center relative group
		${useMasonry ? `${imageWidth}` : 'w-44 aspect-w-1 aspect-h-1'} ${index === images.length - 1 ? `load-more` : ''} overflow-hidden`}
>
	<img
		loading="lazy"
		src={htmlImages[index].src}
		alt={image.text_prompt}
		class={`pointer-events-none object-cover group-hover:opacity-75 ${useMasonry ? `${imageWidth} rounded` : ''}`}
	/>
	{#if useMasonry}
		<div class="h-[2px]"></div>
	{/if}
	<div
		class={`absolute w-full h-full ${assetSelectionIds.includes(image.generation_id) ? 'bg-cyan-400 opacity-15' : ''}`}
	></div>
	<div
		role="button"
		tabindex="0"
		class={`absolute inset-0 focus:outline-none library-image-button ${assetSelectionIds.includes(image.generation_id) ? 'shadow-[inset_0_0_0_2px_#22D3EE]' : ''}`}
		on:click={() => {
			handleAction();
		}}
		on:keydown={(e) => {
			if (e.key === 'Enter') {
				handleAction();
			}
		}}
	>
		<span class="sr-only">View details for asset.</span>
		{#if showPinControls && selectedBoard}
			<div class="pin-controls">
				<PinControls
					{image}
					{selectedBoard}
					{boardIdToBoardImagePinIdsMap}
					{createPin}
					{deletePin}
					{clickBoardSelectorButton}
				/>
			</div>
		{/if}
		{#if showSelectionBoxes}
			<button
				aria-label="Select image"
				class={`absolute bottom-1 left-1 w-10 h-10 rounded
				bg-primary-50 text-primary-50 hover:text-primary-200 hover:brightness-90
				dark:bg-navy-900 dark:text-navy-900 dark:hover:text-zinc-800 dark:hover:brightness-150
				${!assetSelectionIds.includes(image.generation_id) ? 'pin-controls border border-primary-200 dark:border-zinc-800' : 'border-2 border-cyan-400'}`}
				on:click={(e) => {
					e.stopPropagation();
					if (assetSelectionIds.includes(image.generation_id)) {
						assetSelectionIds = assetSelectionIds.filter((_) => _ !== image.generation_id);
					} else {
						assetSelectionIds = [...assetSelectionIds, image.generation_id];
					}
				}}
			>
				{#if assetSelectionIds.includes(image.generation_id)}
					<i class="fa-solid fa-check text-cyan-400"></i>
				{:else}
					<i class="fa-solid fa-check"></i>
				{/if}
			</button>
		{/if}
	</div>
</div>

<style>
	.pin-controls {
		visibility: hidden;
	}

	.library-image-button:hover .pin-controls,
	.pin-controls.active {
		visibility: visible;
	}
</style>
